import { EventPackage } from 'services/api/entities/catalog/packages/eventPackage'
import {
  getEventPackageFormAlreadyCreatedPackagesWithConstraints,
  getEventPackageFormInCreationPackagesWithConstraints,
  getEventPackageFormInCreationPackageTuples,
  PackagesWithMinAndMaxQuantityMap,
} from 'resources/catalog/packages/eventPackage/package/common'
import { include } from 'resources/operations/events/utils'
import { WithMinAndMaxQuantity, WithId } from 'types/common'
import { getById, getId } from '..'
import { ValidateForm } from 'ra-core'
const MIN_PARTICIPANTS_DEFAULT_VALUE = 1
const MAX_PARTICIPANTS_DEFAULT_VALUE = 300

export const getMaxOfMinQuantity = (
  product: Partial<WithMinAndMaxQuantity>[]
) =>
  Math.max(
    ...product.map((e) => e.minQuantity ?? MIN_PARTICIPANTS_DEFAULT_VALUE)
  )

export const getMinOfMaxQuantity = (
  product: Partial<WithMinAndMaxQuantity>[]
) =>
  Math.min(
    ...product.map((e) => e.maxQuantity ?? MAX_PARTICIPANTS_DEFAULT_VALUE)
  )

export const arePackagesQuantityLimitsCompatible =
  (packagesMap: PackagesWithMinAndMaxQuantityMap): ValidateForm =>
  //#ra4Migration fixme: FieldValues
  (form) => ({
    error: hasPackagesWithCompatibleMinAndMaxQuantity(packagesMap)(
      form as EventPackage
    ),
  })
export const hasPackagesWithCompatibleMinAndMaxQuantity =
  (packagesMap: PackagesWithMinAndMaxQuantityMap) => (form: EventPackage) => {
    // in the case of the selecting packages that already exist we build the list of the packages with constraints from the packages map
    const alreadyCreatedPackagesWithConstraints =
      getEventPackageFormAlreadyCreatedPackagesWithConstraints(
        form,
        packagesMap
      )

    // in the case of in creation packages we build the list of the packages that are being created, bound to the key of the package map
    // in order to easily retrieve the correct key later
    const inCreationPackageTuples = getEventPackageFormInCreationPackageTuples(
      form,
      packagesMap
    )

    // then we build the packagers with constraints from the tuples we found earlier
    const inCreationPackagesWithConstraints = inCreationPackageTuples.map(
      getEventPackageFormInCreationPackagesWithConstraints(packagesMap)
    )

    // the form is valid if the constraints of both the already existing packages and the in creation packages are compatible
    return areMinAndMaxCompatibleInCollection([
      ...alreadyCreatedPackagesWithConstraints,
      ...inCreationPackagesWithConstraints,
    ])
  }

export const getMessageIfPhysicalIsNotIdealForLocation =
  (packagesMap: PackagesWithMinAndMaxQuantityMap) =>
  ({ physicalPackage, locationPackage }: EventPackage) => {
    // here also we need to derive the package with constraints if package already exists in the package map,
    // or if we are creating it in the form we need to build a package with constraints on the fly
    const physicalPackageWithConstraints =
      !physicalPackage || typeof physicalPackage === 'number'
        ? getById(
            packagesMap.physicalPackage.packages,
            physicalPackage as number | undefined
          )
        : getEventPackageFormInCreationPackagesWithConstraints(packagesMap)([
            'physicalPackage',
            physicalPackage,
          ])

    // here also we need to derive the package with constraints if package already exists in the package map,
    // or if we are creating it in the form we need to build a package with constraints on the fly
    const locationPackageWithConstraints =
      !locationPackage || typeof locationPackage === 'number'
        ? getById(
            packagesMap.locationPackage.packages,
            locationPackage as number | undefined
          )
        : getEventPackageFormInCreationPackagesWithConstraints(packagesMap)([
            'locationPackage',
            locationPackage,
          ])

    if (
      !physicalPackageWithConstraints?.maxQuantity ||
      !locationPackageWithConstraints?.maxQuantity
    ) {
      return undefined
    }

    return locationPackageWithConstraints.maxQuantity >
      physicalPackageWithConstraints.maxQuantity + 20
      ? 'The capacity of a location is much higher than the maximum threshold of a physical experience, so the location may be too big for this package. Please check the limits of the inserted products.'
      : undefined
  }

const areMinAndMaxCompatible = (min?: number, max?: number) =>
  !max || !min || min < max

export const areMinAndMaxCompatibleInCollection = (
  collection?: Partial<WithMinAndMaxQuantity>[],
  minExtractor = getMaxOfMinQuantity,
  maxExtractor = getMinOfMaxQuantity
) => {
  if (!collection) return undefined

  return areMinAndMaxCompatible(
    minExtractor(collection),
    maxExtractor(collection)
  )
    ? undefined
    : 'You have inserted some products whose min and max quantities are not compatible'
}

export const validatePresetMinAndMaxCompatibility =
  <T extends WithId & Partial<WithMinAndMaxQuantity>>(products: T[]) =>
  (presets?: WithId[]) => {
    return areMinAndMaxCompatibleInCollection(
      include((presets ?? []).map(getId), products)
    )
  }
